import stringHelper from '../Helpers/stringHelper';
import searchItemHelper from '../Helpers/searchItemHelper.ts';
import objectHelper from '../Helpers/objectHelper';

export default class SearchParamsBase {
  // ---- Default search-panel UI config; a subclass overrides any of these ----

  // Persistence key for saved searches (StoredJsonDataService). MANDATORY: a unique, stable
  // string, by convention the class's own name. Falsy here so save/open throws if a class
  // forgot to set it. Not derived from the class name because minification mangles it.
  static saveType = null;

  // MANDATORY for panel-rendered classes: array of field descriptors the panel iterates, e.g.
  //   { fieldName, label, className, type: InputTypesEnum, tab?, hide?, showCondition?, options?, validityChecker? }
  // Falsy (not []) so a class that forgot to define it fails loudly instead of rendering empty.
  static fieldsInSearchBox = null;

  // Font Awesome icon class shown on the search-panel header.
  static iconName = 'fa-solid fa-search';

  // Panel width in pixels; 0 means "use the panel's default/auto width".
  static maxWidth = 0;

  // Label and icon for the panel's "start search" button.
  static startSearchButtonLabel = 'Search';
  static startSearchButtonIconName = 'fa-solid fa-search';

  // Optional multi-tab layout: falsy => single panel; else array of { label, id, icon }, a
  // field placed on a tab by matching its `tab` to a tab `id`.
  static tabsOnTheSearchPanel = null;

  // Optional dependent-field clearing: falsy => disabled; else { values: [ { triggerField, toErase, value } ] }.
  static valuesToEraseOnChange = null;

  // Constructing with no args also loads the URL search query params into the object;
  // pass a truthy value (`new XxxSearchParams(true)`) to skip that.
  constructor(isNew = false) {
    this.initialize();
    if (!isNew) {
      stringHelper.updateSearchParams(this);
    }
  }

  // Sets the search item's default properties. Must be implemented by every subclass.
  initialize() {
    throw new Error('This Search Parameter must implement initialize().');
  }

  // Resets the search item to its defaults (e.g. TabOfDataTable's clear-search action).
  // Override only when a reset should differ from a full re-initialization.
  ClearAll() {
    this.initialize();
  }

  // True when `searchItem` differs from a freshly-constructed default (i.e. the user entered
  // a filter). Used by admin pages to decide whether to auto-open the search panel. Static so
  // `this` resolves to the concrete subclass and `new this()` builds the correct defaults.
  static isThereAnySearchParameter(searchItem) {
    if (!searchItem) return false;
    const defaultSearchItem = new this();
    return Object.keys(searchItem).some((key) => !objectHelper.valuesEqual(searchItem[key], defaultSearchItem[key]));
  }

  // Instance properties that are NOT filter data and must be stripped before sending —
  // only those indistinguishable from real filters by shape (e.g. UI-permission flags used
  // in `hide`/`showCondition`, display placeholders). Methods and statics are already
  // excluded since only own instance properties are walked. Subclasses override with their list.
  fieldsToExcludeFromSerialization() {
    return [];
  }

  // Returns a new object with only the non-"empty" properties (strings trimmed; see
  // searchItemHelper for the rules), minus the fields in fieldsToExcludeFromSerialization().
  cleanForBackendCall() {
    const cleaned = searchItemHelper.removeEmptyValues(this);
    for (const key of this.fieldsToExcludeFromSerialization()) {
      delete cleaned[key];
    }
    return cleaned;
  }
}
